import { reduce, startCase } from 'lodash';

import { Shape2Icon } from '../../components/Shape2/Shape2';
import {
    Dictionary,
    DisplayListItem,
    DisplayRegion,
    Orientation,
    PreviewScreens,
    Screen,
    SelectedScreens
} from '../../typings/displayMap';

export const getCheckedScreens = (
    platforms: DisplayListItem[],
    checkedIds: string[]
): DisplayListItem[] => {
    return platforms.filter((platform: DisplayListItem) =>
        checkedIds.includes(platform.id)
    );
};

export const getPreviewScreens = (
    platforms: DisplayListItem[]
): PreviewScreens => {
    return reduce(
        platforms,
        (result: PreviewScreens, platform: DisplayListItem) => {
            platform.screens.forEach(screen => {
                if (Boolean(screen.isDeleted)) {
                    result.inactiveScreens.push(screen);
                } else {
                    result.activeScreens.push(screen);
                }
            });

            return result;
        },
        { activeScreens: [], inactiveScreens: [] }
    );
};

export const mapRawPlatformsToScreens = (
    platforms: DisplayListItem[],
    dictionary: Dictionary,
    selectedScreens: string[]
): DisplayListItem[] => {
    if (!selectedScreens) {
        return [];
    }

    platforms = platforms.filter((platform: DisplayListItem) =>
        selectedScreens.includes(platform.id)
    );

    return platforms.map((platform: DisplayListItem) => {
        const facilityDictionary = dictionary.facility;
        const zoneDictionary =
            'zone' in dictionary ? dictionary.zone : undefined;
        const facilityName = facilityDictionary[platform.facilityType] || '';
        const zoneCategory =
            (zoneDictionary && zoneDictionary[platform.zoneCategory]) || '';

        return {
            ...platform,
            facilityType: startCase(facilityName),
            zoneCategory: startCase(zoneCategory)
        };
    });
};

export const getScreensCount = (
    screensType: 'activeScreens' | 'inactiveScreens',
    previewScreens: PreviewScreens
): number => {
    return reduce(
        previewScreens[screensType],
        (count: number, screen: Screen) => {
            count += screen.screenWeight;

            return count;
        },
        0
    );
};

export const sortRegions = (regions: DisplayRegion[]): DisplayRegion[] => {
    const sortIdxses = {
        // Москва
        213: 0,
        // Питер
        2: 1,
        // Нижний Новгород
        47: 2,
        // Казань
        43: 3
    };

    const firstPart: DisplayRegion[] = [];
    const secondPart: DisplayRegion[] = [];

    regions.forEach(region => {
        const { id } = region;

        if (id in sortIdxses) {
            firstPart.push(region);
        } else {
            secondPart.push(region);
        }
    });

    return new Array<DisplayRegion>().concat(
        firstPart.sort(({ id: a }, { id: b }) => sortIdxses[a] - sortIdxses[b]),
        secondPart.sort(({ text: a }, { text: b }) => (a > b ? 1 : -1))
    );
};

export const getSelectedPlatformsCount = (
    screenGroupsByRegion: {
        [id: string]: DisplayListItem[];
    },
    selectedScreens: SelectedScreens = {}
): number => {
    return Object.keys(selectedScreens)
        .map((regionId: string) =>
            screenGroupsByRegion[regionId]
                .filter(({ id }) => selectedScreens[regionId].includes(id))
                .reduce(
                    (res, { screens }) =>
                        res +
                        screens.reduce(
                            (acc, { screenWeight }) => screenWeight + acc,
                            0
                        ),
                    0
                )
        )
        .reduce((a, b) => a + b, 0);
};

export const getScreenWeightCount = (screens: Screen[]): number =>
    screens.reduce((count, screen) => count + screen.screenWeight, 0);

export const getScreenOrientationByScreens = (
    screens: Screen[]
): Orientation => {
    return screens.reduce(
        (acc: { vertical: boolean; horizontal: boolean }, screen) => {
            if ('aspectRatio' in screen) {
                if (screen.aspectRatio[0] > screen.aspectRatio[1]) {
                    acc.horizontal = true;
                }
                if (screen.aspectRatio[0] < screen.aspectRatio[1]) {
                    acc.vertical = true;
                }
            }

            return acc;
        },
        { vertical: false, horizontal: false }
    );
};

export const getScreenTypeIconByScreens = (screens: Screen[]): Shape2Icon => {
    return getScreenTypeIconByOrientation(
        getScreenOrientationByScreens(screens)
    );
};

export const getScreenTypeIconByOrientation = (
    orientation: Orientation
): Shape2Icon => {
    let icon: Shape2Icon;

    if (orientation.vertical && orientation.horizontal) {
        icon = Shape2Icon.screenTypeBoth;
    } else if (orientation.vertical) {
        icon = Shape2Icon.screenTypeVertical;
    } else if (orientation.horizontal) {
        icon = Shape2Icon.screenTypeHorizontal;
    } else {
        icon = Shape2Icon.screenTypeBoth;
    }

    return icon;
};
